import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { TierDot } from "@/components/school/TierDot";
import { FitComparison } from "@/components/school/FitComparison";
import {
  formatDivision,
  schoolBrandStyle,
  schoolInitials,
  schoolLocation,
} from "@/components/school/schoolBrand";
import type { PipelineEntry } from "@/services/pipeline";
import {
  PIPELINE_STAGES,
  fitTierLabel,
  nextRelationshipAction,
  stageIndex,
} from "./pipelineDesign";

interface SchoolPortfolioListProps {
  entries: PipelineEntry[];
  onOpen: (entry: PipelineEntry) => void;
}

/** Logo tile for a list row: the school's logo on white, or initials on its color. */
function SchoolMark({ entry, size }: { entry: PipelineEntry; size: "sm" | "md" }) {
  const [failed, setFailed] = useState(false);
  const logoUrl = entry.school.logoUrl;
  useEffect(() => setFailed(false), [logoUrl]);
  const box = size === "md" ? "h-10 w-10 rounded-[10px]" : "h-9 w-9 rounded-[9px]";

  if (logoUrl && !failed) {
    return (
      <span
        className={`grid ${box} shrink-0 place-items-center overflow-hidden border border-border bg-white p-1`}
      >
        <img
          src={logoUrl}
          alt=""
          className="h-full w-full object-contain"
          onError={() => setFailed(true)}
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      style={schoolBrandStyle(entry.school)}
      className={`grid ${box} shrink-0 place-items-center bg-[linear-gradient(135deg,var(--school-primary),var(--school-secondary))] font-display text-[13px] font-extrabold text-[var(--school-ink)]`}
    >
      {schoolInitials(entry.school.schoolName).slice(0, 2)}
    </span>
  );
}

function StageBar({ entry }: { entry: PipelineEntry }) {
  const current = stageIndex(entry.stage);
  return (
    <div>
      <div aria-hidden="true" className="flex gap-[3px]">
        {PIPELINE_STAGES.map((stage, index) => (
          <span
            key={stage.id}
            className={`h-[5px] w-4 rounded-full ${index < current ? "bg-primary" : index === current ? "bg-forest" : "bg-muted"}`}
          />
        ))}
      </div>
      <span className="mt-1 block text-xs font-bold text-forest">
        {PIPELINE_STAGES[current]?.shortLabel}
      </span>
    </div>
  );
}

function scoreOf(entry: PipelineEntry): number | null {
  return entry.golfFit?.status === "unavailable" ? null : (entry.golfFit?.score ?? null);
}

/** Compact My Schools view: one row per school, for scanning long lists. */
export function SchoolPortfolioList({ entries, onOpen }: SchoolPortfolioListProps) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-white">
      <div
        aria-hidden="true"
        className="hidden grid-cols-[minmax(0,2.1fr)_minmax(0,0.85fr)_minmax(0,0.65fr)_minmax(0,1.6fr)_minmax(0,0.85fr)_minmax(0,1.5fr)_16px] items-center gap-4 border-b border-border bg-surface px-4 py-2.5 text-xs font-semibold text-muted-foreground lg:grid"
      >
        <span>School</span>
        <span>Tier</span>
        <span>Golf fit</span>
        <span>You vs recent commits</span>
        <span>Stage</span>
        <span>Next action</span>
        <span />
      </div>
      <ul aria-label="My Schools">
        {entries.map(entry => {
          const evidence = entry.golfFit?.evidence;
          const player = evidence?.playerScoringDifferential;
          const program = evidence?.programAverageScoringDifferential;
          const tier = entry.golfFit?.status === "unavailable" ? null : entry.golfFit?.tier;
          const score = scoreOf(entry);
          const meta = [formatDivision(entry.school.division), schoolLocation(entry.school)]
            .filter(Boolean)
            .join(" · ");

          return (
            <li key={entry._id} className="border-b border-muted last:border-b-0">
              <button
                type="button"
                onClick={() => onOpen(entry)}
                aria-label={`Open ${entry.school.schoolName}`}
                className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-surface focus-visible:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 lg:grid lg:grid-cols-[minmax(0,2.1fr)_minmax(0,0.85fr)_minmax(0,0.65fr)_minmax(0,1.6fr)_minmax(0,0.85fr)_minmax(0,1.5fr)_16px] lg:gap-4 lg:px-4"
              >
                <span className="flex min-w-0 flex-1 items-center gap-3">
                  <SchoolMark entry={entry} size="md" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-forest">
                      {entry.school.schoolName}
                    </span>
                    <span className="mt-0.5 hidden truncate text-xs text-subtle lg:block">
                      {meta}
                    </span>
                    {/* Phone: tier, stage and next action under the name */}
                    <span className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground lg:hidden">
                      {tier && (
                        <span className="inline-flex items-center gap-1 font-bold text-forest">
                          <TierDot tier={tier} />
                          {fitTierLabel(tier)}
                        </span>
                      )}
                      {tier && "·"} {PIPELINE_STAGES[stageIndex(entry.stage)]?.label}
                    </span>
                    <span className="mt-0.5 block truncate text-xs font-semibold text-primary-text lg:hidden">
                      Next: {nextRelationshipAction(entry)}
                    </span>
                  </span>
                </span>

                <span className="hidden lg:block">
                  {tier ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-0.5 text-xs font-bold text-forest">
                      <TierDot tier={tier} />
                      {fitTierLabel(tier)}
                    </span>
                  ) : (
                    <span className="text-xs text-subtle">Assessing</span>
                  )}
                </span>

                <span className="shrink-0 text-right lg:text-left">
                  <span className="block text-lg font-extrabold tabular-nums text-forest lg:inline lg:text-base">
                    {score ?? "—"}
                  </span>
                  {score != null && (
                    <span className="hidden text-xs font-semibold text-subtle lg:inline">/100</span>
                  )}
                  <span className="block text-[11px] font-semibold text-subtle lg:hidden">
                    Golf fit
                  </span>
                </span>

                <span className="hidden lg:block">
                  <FitComparison
                    playerRank={evidence?.playerClassRank}
                    programRank={evidence?.programAverageClassRank}
                    playerDifferential={player}
                    programDifferential={program}
                    variant="compact"
                  />
                  {!evidence && <span className="text-[13px] text-subtle">—</span>}
                </span>

                <span className="hidden lg:block">
                  <StageBar entry={entry} />
                </span>

                <span className="hidden min-w-0 truncate text-xs text-forest-soft lg:block">
                  {nextRelationshipAction(entry)}
                </span>

                <ChevronRight aria-hidden="true" className="hidden h-4 w-4 text-subtle lg:block" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
